import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiCheck, FiCalendar, FiUsers, FiMapPin, FiLoader, FiAlertCircle } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { respondToRoundInvitation } from '../services/roundService';
import { formatDateWithWeekday } from '../utils/dateHelpers';
import { formatRoundDate, formatRoundTimeWithViewerNote } from '../utils/timeHelpers';
import { trackRoundResponseAccept, trackRoundResponseDecline } from '../services/analytics';
import { hapticSuccess, hapticImpact } from '../services/hapticsService';
import { useAuth } from '../contexts/AuthContext';

// Copy for every non-'updated' outcome the RPC can return. Rendered inline in
// the modal rather than thrown, so the user sees why nothing happened.
const OUTCOME_MESSAGES = {
  round_full: 'This round filled up while you were away. Ask the organiser to make space.',
  round_started: 'This round has already started, so responses are closed.',
  round_cancelled: 'This round has been cancelled.',
  awaiting_host: 'The host is still reviewing your request to join.',
  not_invited: 'You are not on the invite list for this round.',
};

function RoundResponseModal({ isOpen, onClose, round, currentStatus = null, onResponseSuccess }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDeclineReason, setShowDeclineReason] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  // A response already on record turns this into an edit rather than a first
  // answer. Guests have had this since the guest-link work; members now match.
  const isChange = currentStatus === 'accepted' || currentStatus === 'declined';
  const currentLabel = currentStatus === 'accepted' ? 'Accepted' : 'Declined';

  const formatTime = (timeString) => {
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      return `${displayHour}:${minutes} ${period}`;
    } catch {
      return timeString;
    }
  };

  const handleResponse = async (status, reason = null) => {
    setLoading(true);
    setError('');

    try {
      const { data, error } = await respondToRoundInvitation(round.id, status, reason);

      if (error) {
        console.error('Error responding to invitation:', error);
        setError(error.message || 'Failed to respond to invitation');
        setLoading(false);
        return;
      }

      // The RPC reports refusals as an outcome rather than an error, so the
      // user gets specific copy instead of a generic failure.
      if (data?.outcome && data.outcome !== 'updated' && data.outcome !== 'unchanged') {
        setError(OUTCOME_MESSAGES[data.outcome] || 'That response could not be recorded.');
        setLoading(false);
        return;
      }

      if (user?.id && round?.id) {
        if (status === 'accepted') {
          trackRoundResponseAccept(user.id, round.id);
          hapticSuccess();
        } else if (status === 'declined') {
          trackRoundResponseDecline(user.id, round.id);
          hapticImpact('medium');
        }
      }

      if (onResponseSuccess) {
        await onResponseSuccess();
      }

      handleClose();
    } catch (err) {
      console.error('Exception responding to invitation:', err);
      setError('Failed to respond to invitation. Please try again.');
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      setShowDeclineReason(false);
      setDeclineReason('');
      onClose();
    }
  };

  if (!isOpen || !round) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-[60]"
        onClick={handleClose}
      >
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-card rounded-t-xl sm:rounded-xl shadow-modal max-w-md w-full mb-20 sm:mb-0"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-5">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-text-primary">
                  {isChange ? 'Change your response' : 'Round Invitation'}
                </h2>
                <p className="text-xs text-text-muted mt-0.5">
                  {isChange ? `Currently: ${currentLabel}` : 'Respond to this round'}
                </p>
              </div>
              <button
                onClick={handleClose}
                disabled={loading}
                className="w-8 h-8 bg-surface rounded-lg flex items-center justify-center hover:bg-border-subtle transition-colors disabled:opacity-50"
              >
                <SafeIcon icon={FiX} className="w-4 h-4 text-text-secondary" />
              </button>
            </div>

            {/* Round Details */}
            <div className="bg-card border border-border-subtle rounded-xl overflow-hidden mb-4">
              <div className="px-4 py-3 border-b border-border-subtle">
                <div className="flex items-center text-sm font-bold text-text-primary">
                  <SafeIcon icon={FiUsers} className="w-4 h-4 mr-2 text-accent" />
                  {round.groupName}
                </div>
              </div>

              <div className="divide-y divide-border-subtle">
                <div className="flex items-center px-4 py-3">
                  <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center mr-3">
                    <SafeIcon icon={FiCalendar} className="w-3.5 h-3.5 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">
                      {formatRoundDate(round, { weekday: 'short', day: 'numeric', month: 'short', year: undefined })}
                    </p>
                    {(() => {
                      if (!round.time) {
                        return <p className="text-xs text-text-secondary">Time TBD</p>;
                      }
                      const { primary, secondary } = formatRoundTimeWithViewerNote(round, { hour12: true });
                      return (
                        <>
                          <p className="text-xs text-text-secondary">{primary}</p>
                          {secondary && <p className="text-xs text-text-tertiary">{secondary}</p>}
                        </>
                      );
                    })()}
                  </div>
                </div>

                {round.course && (
                  <div className="flex items-center px-4 py-3">
                    <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center mr-3">
                      <SafeIcon icon={FiMapPin} className="w-3.5 h-3.5 text-accent" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-text-primary">{round.course}</p>
                      <p className="text-xs text-text-secondary">Golf Course</p>
                    </div>
                  </div>
                )}

                {round.round_invitations && (
                  <div className="flex items-center px-4 py-3">
                    <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center mr-3">
                      <SafeIcon icon={FiUsers} className="w-3.5 h-3.5 text-accent" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-text-primary">
                        {round.round_invitations.filter(inv => inv.status === 'accepted').length} confirmed,{' '}
                        {round.round_invitations.filter(inv => inv.status === 'pending').length} pending
                      </p>
                      <p className="text-xs text-text-secondary">Current responses</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Error */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-status-error rounded-xl text-sm flex items-center"
              >
                <SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-2 flex-shrink-0" />
                {error}
              </motion.div>
            )}

            {/* Actions. In change mode the answer already on record is shown as
                a disabled ghost row, so the only live control is the one that
                actually changes something. */}
            {!showDeclineReason ? (
              <div className="space-y-2">
                {currentStatus === 'accepted' ? (
                  <div className="w-full flex items-center justify-center px-5 py-3 bg-surface text-text-muted rounded-xl text-sm font-medium">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2" />
                    Accepted
                  </div>
                ) : (
                  <button
                    onClick={() => handleResponse('accepted')}
                    disabled={loading}
                    className="w-full flex items-center justify-center px-5 py-3 bg-accent text-white rounded-xl hover:bg-interactive-hover transition-colors text-sm font-bold disabled:opacity-50"
                  >
                    {loading ? (
                      <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2" />
                        {isChange ? 'Change to accepted' : 'Accept Invitation'}
                      </>
                    )}
                  </button>
                )}

                {currentStatus === 'declined' ? (
                  <div className="w-full flex items-center justify-center px-5 py-3 bg-surface text-text-muted rounded-xl text-sm font-medium">
                    <SafeIcon icon={FiX} className="w-4 h-4 mr-2" />
                    Declined
                  </div>
                ) : (
                  <button
                    onClick={() => setShowDeclineReason(true)}
                    disabled={loading}
                    className={`w-full flex items-center justify-center px-5 py-3 rounded-xl transition-colors text-sm disabled:opacity-50 ${
                      isChange
                        ? 'bg-accent text-white hover:bg-interactive-hover font-bold'
                        : 'border border-border-subtle text-text-secondary hover:bg-surface font-medium'
                    }`}
                  >
                    <SafeIcon icon={FiX} className="w-4 h-4 mr-2" />
                    {isChange ? 'Change to declined' : 'Decline Invitation'}
                  </button>
                )}

                <button
                  onClick={handleClose}
                  disabled={loading}
                  className="w-full px-5 py-2 text-text-muted hover:text-text-secondary transition-colors text-xs font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <label htmlFor="decline-reason" className="block text-xs font-medium text-text-secondary">
                  Add a reason (optional)
                </label>
                <textarea
                  id="decline-reason"
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  maxLength={300}
                  rows={3}
                  disabled={loading}
                  placeholder="Let the organiser know why you can't make it"
                  className="w-full px-3 py-2 bg-app border border-border-subtle rounded-xl text-sm text-text-primary placeholder-text-muted focus:ring-2 focus:ring-accent focus:border-transparent transition-all resize-y disabled:opacity-50"
                />
                <button
                  onClick={() => handleResponse('declined', declineReason.trim() || null)}
                  disabled={loading}
                  className="w-full flex items-center justify-center px-5 py-3 bg-accent text-white rounded-xl hover:bg-interactive-hover transition-colors text-sm font-bold disabled:opacity-50"
                >
                  {loading ? (
                    <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                  ) : (
                    'Confirm decline'
                  )}
                </button>
                <button
                  onClick={() => setShowDeclineReason(false)}
                  disabled={loading}
                  className="w-full px-5 py-2 text-text-muted hover:text-text-secondary transition-colors text-xs font-medium disabled:opacity-50"
                >
                  Back
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default RoundResponseModal;
